import {
	CancelledError,
	type EnsureQueryDataOptions,
	type QueryClient,
	type QueryKey,
} from "@tanstack/react-query";

const MAX_CANCELLED_ATTEMPTS = 3;

/**
 * `ensureQueryData` that outlives a revert cancel. When the last observer of an
 * in-flight query unmounts (the Full → Offline swap while a page loads), the
 * query cancels itself and the awaited promise rejects with `CancelledError`,
 * which would fail the loader and render the org error page. The cancelled query
 * is back in its previous state, so asking again resolves from cache or restarts
 * the fetch.
 */
export async function ensureQueryDataSurvivingCancel<
	TQueryFnData,
	TError,
	TData,
	TQueryKey extends QueryKey,
>(
	queryClient: QueryClient,
	options: EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>,
) {
	for (let attempt = 1; ; attempt++) {
		try {
			return await queryClient.ensureQueryData(options);
		} catch (error) {
			if (
				!(error instanceof CancelledError) ||
				attempt >= MAX_CANCELLED_ATTEMPTS
			) {
				throw error;
			}
		}
	}
}
